import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import type { Tool, User } from "@/lib/types";
import { ToolCard } from "./ToolCard";

interface MyToolsPageProps {
	currentUserId: string;
	onViewTool: (toolId: string) => void;
	onListTool: () => void;
}

export function MyToolsPage({ currentUserId, onViewTool, onListTool }: MyToolsPageProps) {
	const [tools, setTools] = useState<(Tool & { owner?: User })[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadTools();
	}, []);

	async function loadTools() {
		setLoading(true);
		try {
			const allTools = await api.getTools();
			setTools(allTools.filter((t) => t.ownerId === currentUserId));
		} catch (e) {
			console.error("Failed to load tools:", e);
		} finally {
			setLoading(false);
		}
	}

	if (loading) {
		return (
			<div className="space-y-4">
				<div className="h-8 w-48 bg-muted rounded animate-pulse" />
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="h-64 bg-muted rounded-lg animate-pulse" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h1 className="text-xl font-bold flex items-center gap-2">
					<Wrench className="w-5 h-5" />
					My Tools
					<Badge variant="secondary">{tools.length}</Badge>
				</h1>
				<Button size="sm" onClick={onListTool} className="gap-2">
					<Plus className="w-4 h-4" />
					List a Tool
				</Button>
			</div>

			{tools.length === 0 ? (
				<Card>
					<CardContent className="p-12 text-center">
						<Wrench className="w-12 h-12 mx-auto text-muted-foreground/30" />
						<h3 className="font-medium mt-4">No tools listed yet</h3>
						<p className="text-sm text-muted-foreground mt-1">
							List your first tool to start earning from equipment you're not using.
						</p>
						<Button className="mt-4 gap-2" onClick={onListTool}>
							<Plus className="w-4 h-4" />
							List your first tool
						</Button>
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
					{tools.map((tool) => (
						<ToolCard
							key={tool.id}
							tool={tool}
							onClick={() => onViewTool(tool.id)}
						/>
					))}
				</div>
			)}
		</div>
	);
}
